import { API_ENDPOINTS } from '../../../constants';
import type {
  Friend,
  FriendSuggestion,
  FriendSuggestionsResponse,
  PickHistoryEntry,
  MatchApiResponse,
  FriendStatsResponse,
} from '../types/friends.types';
import { ChallengeWithStats } from '../../../types/challenges';
import { fetchActiveChallenges as fetchChallengesFromSharedApi } from '../../challenges/helpers/challengesApi';
import type { AxiosResponse } from 'axios';
import { extractErrorMessage, isNetworkError } from '../../../helpers/apiClient.helper';
import { logger } from '../../helpers/logger.helper';

/**
 * Fetch friends from backend API
 * Requires authenticated API client (Supabase JWT token)
 */
export const fetchFriends = async (
  userId: string,
  apiClientFn: <T = any>(endpoint: string, config?: any) => Promise<AxiosResponse<T>>
): Promise<Friend[]> => {
  try {
    const friendsEndpoint = `${API_ENDPOINTS.users.friends}/${userId}`;
    const { data } = await apiClientFn<{ success: boolean; data?: Friend[] }>(friendsEndpoint);

    if (data?.success && data.data) {
      return data.data;
    }
    return [];
  } catch (error) {
    const msg = extractErrorMessage(error, 'Failed to fetch friends');
    if (isNetworkError(error)) {
      logger.warn('fetchFriends failed: server unreachable');
    } else {
      logger.error('fetchFriends failed', { message: msg });
    }
    throw error;
  }
};

/**
 * Fetch recommended people ("People you might know") from the internal social graph.
 * Requires authenticated API client (Supabase JWT token).
 */
export const fetchFriendSuggestions = async (
  apiClientFn: <T = any>(endpoint: string, config?: any) => Promise<AxiosResponse<T>>,
  limit?: number,
  offset?: number
): Promise<FriendSuggestion[]> => {
  try {
    const params = new URLSearchParams();
    if (typeof limit === 'number') params.set('limit', String(limit));
    if (typeof offset === 'number') params.set('offset', String(offset));
    const query = params.toString();
    const endpoint = query
      ? `${API_ENDPOINTS.users.friendSuggestions}?${query}`
      : API_ENDPOINTS.users.friendSuggestions;

    const { data } = await apiClientFn<FriendSuggestionsResponse>(endpoint);

    if (data?.success && data.data) {
      return data.data;
    }
    return [];
  } catch (error) {
    const msg = extractErrorMessage(error, 'Failed to fetch friend suggestions');
    if (isNetworkError(error)) {
      logger.warn('fetchFriendSuggestions failed: server unreachable');
    } else {
      logger.error('fetchFriendSuggestions failed', { message: msg });
    }
    throw error;
  }
};

/**
 * Fetch pick history from backend API
 * Requires authenticated API client (Supabase JWT token)
 */
export const fetchPickHistory = async (
  userId: string,
  apiClientFn: <T = any>(endpoint: string, config?: any) => Promise<AxiosResponse<T>>
): Promise<PickHistoryEntry[]> => {
  try {
    const historyEndpoint = `${API_ENDPOINTS.users.pickHistory}/${userId}`;
    const { data } = await apiClientFn<{ success: boolean; data?: PickHistoryEntry[] }>(historyEndpoint);

    if (data?.success && data.data) {
      return data.data;
    }
    return [];
  } catch (error) {
    const msg = extractErrorMessage(error, 'Failed to fetch pick history');
    if (isNetworkError(error)) {
      logger.warn('fetchPickHistory failed: server unreachable');
    } else {
      logger.error('fetchPickHistory failed', { message: msg });
    }
    return [];
  }
};

/**
 * Fetch active challenges from backend API
 * Requires authenticated API client (Supabase JWT token)
 * Uses the shared fetchActiveChallenges from challengesApi to ensure consistency
 */
export const fetchActiveChallenges = async (
  userId: string,
  apiClientFn: <T = any>(endpoint: string, config?: any) => Promise<AxiosResponse<T>>,
  status?: string
): Promise<ChallengeWithStats[]> => {
  return fetchChallengesFromSharedApi(userId, apiClientFn, status);
};

/**
 * Fetch matches from backend API
 * Requires authenticated API client (Supabase JWT token)
 * Can fetch all matches or specific matches by IDs using bulk endpoint
 */
export const fetchMatches = async (
  apiClientFn: <T = any>(endpoint: string, config?: any) => Promise<AxiosResponse<T>>,
  matchIds?: string[]
): Promise<MatchApiResponse[]> => {
  try {
    // If specific match IDs are provided, use bulk-matches endpoint (POST to avoid URL length limit)
    if (matchIds && matchIds.length > 0) {
      const { data } = await apiClientFn<{ success: boolean; data?: MatchApiResponse[] }>('/matches/bulk-matches', {
        method: 'POST',
        data: { ids: matchIds },
      });

      if (data?.success && Array.isArray(data.data)) {
        return data.data as MatchApiResponse[];
      }
      return [];
    }

    // Otherwise, fetch all matches (default behavior)
    const { data } = await apiClientFn<{ success: boolean; data?: MatchApiResponse[] }>('/matches');

    if (data?.success && Array.isArray(data.data)) {
      return data.data as MatchApiResponse[];
    }
    return [];
  } catch (error) {
    const msg = extractErrorMessage(error, 'Failed to fetch matches');
    if (isNetworkError(error)) {
      logger.warn('fetchMatches failed: server unreachable');
    } else {
      logger.error('fetchMatches failed', { message: msg });
    }
    return [];
  }
};

const DEDUPE_MS = 500;

/** In-flight and recent result cache to avoid duplicate requests (e.g. React Strict Mode double mount). */
const friendStatsCache = new Map<
  string,
  { promise: Promise<FriendStatsResponse | null>; result?: FriendStatsResponse | null; at?: number }
>();

function fetchFriendStatsOnce(
  friendId: string,
  page: number,
  limit: number,
  apiClientFn: <T = unknown>(endpoint: string, config?: unknown) => Promise<AxiosResponse<T>>,
  options?: { debug?: boolean }
): Promise<FriendStatsResponse | null> {
  try {
    const debugParam = options?.debug ? '&debug=1' : '';
    const statsEndpoint = `/users/friends/${friendId}/stats?page=${page}&limit=${limit}${debugParam}`;
    return apiClientFn<{ success: boolean; data?: FriendStatsResponse }>(statsEndpoint).then(({ data }) => {
      if (data?.success && data.data) return data.data;
      if (process.env.NODE_ENV === 'development') {
        logger.warn('fetchFriendStats: Response missing success or data field', { data });
      }
      return null;
    }).catch((error) => {
      if (isNetworkError(error)) {
        logger.warn('fetchFriendStats failed: server unreachable');
      } else {
        const status = (error as { response?: { status?: number } }).response?.status;
        logger.error('Stats API Error', { status, endpoint: 'friendStats' });
      }
      throw error;
    });
  } catch {
    return Promise.resolve(null);
  }
}

/**
 * Fetch friend statistics (head-to-head stats) from backend API
 * Requires authenticated API client (Supabase JWT token)
 * Deduplicates in-flight and returns recent result within DEDUPE_MS to avoid duplicate call on remount
 */
export const fetchFriendStats = async (
  friendId: string,
  page: number = 0,
  limit: number = 15,
  apiClientFn: <T = any>(endpoint: string, config?: any) => Promise<AxiosResponse<T>>,
  options?: { debug?: boolean }
): Promise<FriendStatsResponse | null> => {
  const key = `${friendId}:${page}`;
  const now = Date.now();
  const entry = friendStatsCache.get(key);

  if (entry) {
    if (entry.result !== undefined && entry.at != null && now - entry.at < DEDUPE_MS) {
      return Promise.resolve(entry.result);
    }
    return entry.promise;
  }

  const promise = fetchFriendStatsOnce(friendId, page, limit, apiClientFn, options);
  friendStatsCache.set(key, { promise });
  promise
    .then((result) => {
      const e = friendStatsCache.get(key);
      if (e && e.promise === promise) {
        e.result = result;
        e.at = Date.now();
        setTimeout(() => friendStatsCache.delete(key), DEDUPE_MS);
      }
    })
    .catch(() => {
      const e = friendStatsCache.get(key);
      if (e && e.promise === promise) {
        e.result = null;
        e.at = Date.now();
        setTimeout(() => friendStatsCache.delete(key), DEDUPE_MS);
      }
    });
  return promise;
};

/**
 * Send an in-app friend request to an existing user by their ID.
 * Creates a FriendInvitation on the backend (no email sent).
 * Caller should invalidate friends query on success.
 */
export const sendFriendRequest = async (
  targetUserId: string,
  apiClientFn: <T = unknown>(endpoint: string, config?: { method: string; data?: unknown }) => Promise<AxiosResponse<T>>
): Promise<{ success: boolean; invitationId?: string; message?: string }> => {
  try {
    const endpoint = `${API_ENDPOINTS.users.friends}/request`
    const { data } = await apiClientFn<{ success: boolean; invitationId?: string; message?: string }>(
      endpoint,
      { method: 'POST', data: { targetUserId } }
    )
    return { success: data?.success === true, invitationId: data?.invitationId, message: data?.message }
  } catch (error) {
    const msg = extractErrorMessage(error, 'Failed to send friend request')
    return { success: false, message: msg }
  }
};
